import type { CampaignPeriodPrice } from '@btsis/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { uploadCampaignImage } from './campaign-image-upload';

type CategoryListInput = Parameters<typeof api.campaignCategory.list>[0];
type CampaignListInput = Parameters<typeof api.campaign.list>[0];
type PeriodListInput = Parameters<typeof api.campaign.period.list>[0];
type PriceUpsertInput = Parameters<typeof api.campaign.period.prices.upsert>[0];
type FormPublishInput = Parameters<typeof api.campaign.formSchema.publish>[0];

export const campaignCategoryKeys = {
  all: ['campaignCategories'] as const,
  list: (filters: CategoryListInput) => ['campaignCategories', 'list', filters] as const,
  options: ['campaignCategories', 'options'] as const,
};

export const campaignKeys = {
  all: ['campaigns'] as const,
  list: (filters: CampaignListInput) => ['campaigns', 'list', filters] as const,
  detail: (id: string) => ['campaigns', 'detail', id] as const,
  navigation: ['campaigns', 'navigation'] as const,
};

export const campaignPeriodKeys = {
  all: ['campaignPeriods'] as const,
  list: (filters: PeriodListInput) => ['campaignPeriods', 'list', filters] as const,
  detail: (id: string) => ['campaignPeriods', 'detail', id] as const,
  prices: (periodId: string, assignmentId?: string) =>
    ['campaignPeriods', 'prices', periodId, assignmentId ?? 'period'] as const,
  allPrices: (periodId: string) => ['campaignPeriods', 'prices', periodId] as const,
  totals: (periodId: string) => ['campaignPeriods', 'totals', periodId] as const,
};

/**
 * A write on either side changes the menu, so both mutation hooks invalidate
 * the navigation query as well as their own list. The server busts its Redis
 * generation in the same call, so the refetch cannot read a stale menu.
 */
function invalidateEverything(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: campaignCategoryKeys.all });
  queryClient.invalidateQueries({ queryKey: campaignKeys.all });
}

export function useCampaignCategories(filters: CategoryListInput) {
  return useQuery({
    queryKey: campaignCategoryKeys.list(filters),
    queryFn: () => api.campaignCategory.list(filters),
    placeholderData: (previous) => previous,
  });
}

/** The picker's source: active categories only, kept for a few minutes. */
export function useCampaignCategoryOptions() {
  return useQuery({
    queryKey: campaignCategoryKeys.options,
    queryFn: () => api.campaignCategory.list({ page: 1, pageSize: 200, includeInactive: false }),
    staleTime: 5 * 60_000,
  });
}

export function useCreateCampaignCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.campaignCategory.create>[0]) =>
      api.campaignCategory.create(input),
    onSuccess: () => invalidateEverything(queryClient),
  });
}

export function useUpdateCampaignCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.campaignCategory.update>[0]) =>
      api.campaignCategory.update(input),
    onSuccess: () => invalidateEverything(queryClient),
  });
}

export function useReorderCampaignCategories() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.campaignCategory.reorder({ ids }),
    onSuccess: () => invalidateEverything(queryClient),
  });
}

export function useCampaigns(filters: CampaignListInput) {
  return useQuery({
    queryKey: campaignKeys.list(filters),
    queryFn: () => api.campaign.list(filters),
    placeholderData: (previous) => previous,
  });
}

export function useCampaign(id: string | null) {
  return useQuery({
    queryKey: campaignKeys.detail(id ?? ''),
    queryFn: () => api.campaign.get({ id: id as string }),
    enabled: id !== null,
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.campaign.create>[0]) => api.campaign.create(input),
    onSuccess: () => invalidateEverything(queryClient),
  });
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.campaign.update>[0]) => api.campaign.update(input),
    onSuccess: (result) => {
      invalidateEverything(queryClient);
      queryClient.setQueryData(campaignKeys.detail(result.campaign.id), result);
    },
  });
}

export function useSetCampaignImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; file: File }) => uploadCampaignImage(input),
    onSuccess: (result) => {
      invalidateEverything(queryClient);
      queryClient.setQueryData(campaignKeys.detail(result.campaign.id), result);
    },
  });
}

export function useRemoveCampaignImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string }) => api.campaign.removeImage(input),
    onSuccess: (result) => {
      invalidateEverything(queryClient);
      queryClient.setQueryData(campaignKeys.detail(result.campaign.id), result);
    },
  });
}

export function useSetCampaignArchived() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; isArchived: boolean }) =>
      input.isArchived
        ? api.campaign.archive({ id: input.id })
        : api.campaign.restore({ id: input.id }),
    onSuccess: () => invalidateEverything(queryClient),
  });
}

/**
 * The list screen's bulk archive and restore. One call carries the selection
 * and answers per row, so the toolbar never loops a single call per row — see
 * .claude/rules/list-screen-pattern.md.
 */
export function useSetCampaignsArchived() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.campaign.setArchived>[0]) =>
      api.campaign.setArchived(input),
    onSuccess: () => invalidateEverything(queryClient),
  });
}

export function useCampaignPeriods(filters: PeriodListInput, enabled = true) {
  return useQuery({
    queryKey: campaignPeriodKeys.list(filters),
    queryFn: () => api.campaign.period.list(filters),
    enabled,
    placeholderData: (previous) => previous,
  });
}

/**
 * A period write changes the season table and the campaign header that lists
 * the seasons; a status change also changes the menu, which lives under the
 * campaign keys. Prices are a separate key so an edit to one row does not
 * refetch every season.
 */
function invalidatePeriods(queryClient: ReturnType<typeof useQueryClient>, campaignId: string) {
  queryClient.invalidateQueries({ queryKey: ['campaignPeriods', 'list'] });
  queryClient.invalidateQueries({ queryKey: ['campaignPeriods', 'detail'] });
  queryClient.invalidateQueries({ queryKey: ['campaignPeriods', 'totals'] });
  queryClient.invalidateQueries({ queryKey: campaignKeys.detail(campaignId) });
}

export function useCreateCampaignPeriod(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.campaign.period.create>[0]) =>
      api.campaign.period.create(input),
    onSuccess: () => invalidatePeriods(queryClient, campaignId),
  });
}

export function useUpdateCampaignPeriod(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.campaign.period.update>[0]) =>
      api.campaign.period.update(input),
    onSuccess: () => {
      invalidatePeriods(queryClient, campaignId);
      queryClient.invalidateQueries({ queryKey: campaignKeys.navigation });
    },
  });
}

export function useTransitionCampaignPeriod(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.campaign.period.transition>[0]) =>
      api.campaign.period.transition(input),
    onSuccess: (result) => {
      invalidatePeriods(queryClient, campaignId);
      queryClient.invalidateQueries({ queryKey: campaignKeys.all });
      queryClient.invalidateQueries({ queryKey: campaignPeriodKeys.prices(result.period.id) });
    },
  });
}

export function useDuplicateCampaignPeriod(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.campaign.period.duplicate>[0]) =>
      api.campaign.period.duplicate(input),
    onSuccess: () => invalidatePeriods(queryClient, campaignId),
  });
}

export function useCampaignPeriod(id: string | null) {
  return useQuery({
    queryKey: campaignPeriodKeys.detail(id ?? ''),
    queryFn: () => api.campaign.period.get({ id: id as string }),
    enabled: id !== null,
  });
}

/**
 * With an assignment, that assignment's overrides; without one, the season's
 * own prices — docs/decisions/0068-a-price-may-be-overridden-per-assignment.md.
 * Both lists are invalidated together, because an override's currency must
 * still be one the season prices.
 */
export function useCampaignPeriodPrices(periodId: string | null, assignmentId?: string) {
  return useQuery({
    queryKey: campaignPeriodKeys.prices(periodId ?? '', assignmentId),
    queryFn: () =>
      api.campaign.period.prices.list({
        campaignPeriodId: periodId as string,
        pageSize: 50,
        ...(assignmentId ? { campaignAssignmentId: assignmentId } : {}),
      }),
    enabled: periodId !== null,
  });
}

export function useUpsertCampaignPeriodPrice(periodId: string, assignmentId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<PriceUpsertInput, 'campaignPeriodId'>) =>
      api.campaign.period.prices.upsert({
        ...input,
        campaignPeriodId: periodId,
        ...(assignmentId ? { campaignAssignmentId: assignmentId } : {}),
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: campaignPeriodKeys.allPrices(periodId) }),
  });
}

export function useRemoveCampaignPeriodPrice(periodId: string, assignmentId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (price: Pick<CampaignPeriodPrice, 'currency' | 'answerConditions'>) =>
      api.campaign.period.prices.remove({
        campaignPeriodId: periodId,
        currency: price.currency,
        answerConditions: price.answerConditions,
        ...(assignmentId ? { campaignAssignmentId: assignmentId } : {}),
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: campaignPeriodKeys.allPrices(periodId) }),
  });
}

export const campaignFormKeys = {
  all: ['campaignForms'] as const,
  campaign: (campaignId: string) => ['campaignForms', campaignId] as const,
  schema: (campaignId: string, version: number | null) =>
    ['campaignForms', campaignId, 'schema', version ?? 'current'] as const,
  versions: (campaignId: string, page: number, pageSize: number) =>
    ['campaignForms', campaignId, 'versions', { page, pageSize }] as const,
};

/** `version` null is the current published document, which is what the builder edits from. */
export function useCampaignFormSchema(campaignId: string, version: number | null, enabled = true) {
  return useQuery({
    queryKey: campaignFormKeys.schema(campaignId, version),
    enabled,
    queryFn: () =>
      api.campaign.formSchema.get({ campaignId, ...(version === null ? {} : { version }) }),
    staleTime: version === null ? 0 : Number.POSITIVE_INFINITY,
  });
}

export function useCampaignFormVersions(campaignId: string, page: number, pageSize: number) {
  return useQuery({
    queryKey: campaignFormKeys.versions(campaignId, page, pageSize),
    queryFn: () => api.campaign.formSchema.versions({ campaignId, page, pageSize }),
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });
}

/**
 * A dry run changes nothing, so only a real publish invalidates: the form
 * queries of this campaign, and the campaign detail and list that show its
 * form version.
 */
export function usePublishCampaignForm(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<FormPublishInput, 'campaignId'>) =>
      api.campaign.formSchema.publish({ ...input, campaignId }),
    onSuccess: (result) => {
      if (result.dryRun) return;
      queryClient.invalidateQueries({ queryKey: campaignFormKeys.campaign(campaignId) });
      queryClient.invalidateQueries({ queryKey: campaignKeys.detail(campaignId) });
      queryClient.invalidateQueries({ queryKey: ['campaigns', 'list'] });
    },
  });
}

export function useCampaignPeriodTotals(periodId: string | null) {
  return useQuery({
    queryKey: campaignPeriodKeys.totals(periodId ?? ''),
    queryFn: () => api.campaign.period.totals({ id: periodId as string }),
    enabled: periodId !== null,
  });
}

export function useRecomputeCampaignPeriodTotals(periodId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.campaign.period.recomputeTotals({ id: periodId }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: campaignPeriodKeys.totals(periodId) }),
  });
}

export const campaignAssignmentKeys = {
  all: ['campaignAssignments'] as const,
  period: (periodId: string) => ['campaignAssignments', 'list', periodId] as const,
  list: (periodId: string, page: number, pageSize: number) =>
    ['campaignAssignments', 'list', periodId, { page, pageSize }] as const,
  institutionOptions: (periodId: string, search: string) =>
    ['campaignAssignments', 'institutionOptions', periodId, search] as const,
  eligible: (periodId: string, currency: string, answers: string) =>
    ['campaignAssignments', 'eligible', periodId, currency, answers] as const,
};

export function useCampaignAssignments(periodId: string | null, page: number, pageSize: number) {
  return useQuery({
    queryKey: campaignAssignmentKeys.list(periodId ?? '', page, pageSize),
    queryFn: () =>
      api.campaign.assignment.list({ campaignPeriodId: periodId as string, page, pageSize }),
    enabled: periodId !== null,
    placeholderData: (previous) => previous,
  });
}

export function useAssignmentInstitutionOptions(periodId: string, search: string) {
  return useQuery({
    queryKey: campaignAssignmentKeys.institutionOptions(periodId, search),
    queryFn: () =>
      api.campaign.assignment.institutionOptions({
        campaignPeriodId: periodId,
        search: search || undefined,
        pageSize: 20,
      }),
    placeholderData: (previous) => previous,
  });
}

/**
 * Which yurts may execute these answers, each priced. The answers are the key,
 * so changing one asks again — the server decides eligibility and the price,
 * exactly as the save will (docs/decisions/0069-an-assignment-says-what-it-can-execute-and-the-campaign-says-which-answers-decide.md).
 */
export function useEligibleAssignments(input: {
  campaignPeriodId: string | null;
  currency: string;
  formData: Record<string, unknown>;
  quantity?: number | undefined;
  amountMinor?: string | undefined;
}) {
  const answers = JSON.stringify([
    input.formData,
    input.quantity ?? null,
    input.amountMinor ?? null,
  ]);
  return useQuery({
    queryKey: campaignAssignmentKeys.eligible(
      input.campaignPeriodId ?? '',
      input.currency,
      answers,
    ),
    queryFn: () =>
      api.campaign.assignment.eligible({
        campaignPeriodId: input.campaignPeriodId as string,
        currency: input.currency as Parameters<
          typeof api.campaign.assignment.eligible
        >[0]['currency'],
        formData: input.formData,
        ...(input.quantity === undefined ? {} : { quantity: input.quantity }),
        ...(input.amountMinor === undefined ? {} : { amountMinor: input.amountMinor }),
        page: 1,
        pageSize: 200,
      }),
    enabled: input.campaignPeriodId !== null,
    placeholderData: (previous) => previous,
  });
}

/**
 * An assignment write changes this period's list and which institutions the
 * picker marks as assigned; nothing else reads assignments yet.
 */
function invalidateAssignments(queryClient: ReturnType<typeof useQueryClient>, periodId: string) {
  queryClient.invalidateQueries({ queryKey: campaignAssignmentKeys.period(periodId) });
  queryClient.invalidateQueries({
    queryKey: ['campaignAssignments', 'institutionOptions', periodId],
  });
  // Capabilities decide which yurts the entry form offers, so a change to them
  // invalidates that answer too.
  queryClient.invalidateQueries({ queryKey: ['campaignAssignments', 'eligible', periodId] });
}

export function useCreateCampaignAssignment(periodId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      input: Omit<Parameters<typeof api.campaign.assignment.create>[0], 'campaignPeriodId'>,
    ) => api.campaign.assignment.create({ ...input, campaignPeriodId: periodId }),
    onSuccess: () => invalidateAssignments(queryClient, periodId),
  });
}

export function useUpdateCampaignAssignment(periodId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.campaign.assignment.update>[0]) =>
      api.campaign.assignment.update(input),
    onSuccess: () => invalidateAssignments(queryClient, periodId),
  });
}

export function useRemoveCampaignAssignment(periodId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.campaign.assignment.remove({ id }),
    onSuccess: () => invalidateAssignments(queryClient, periodId),
  });
}

export function useSetAssignmentCapabilities(periodId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { assignmentId: string; capabilities: string[] }) =>
      api.campaign.assignment.capabilities.set(input),
    onSuccess: () => invalidateAssignments(queryClient, periodId),
  });
}

export function useCopyCampaignAssignments(periodId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sourcePeriodId: string) =>
      api.campaign.assignment.copyFromPeriod({ campaignPeriodId: periodId, sourcePeriodId }),
    onSuccess: () => invalidateAssignments(queryClient, periodId),
  });
}
